import { HTTP_INTERCEPTORS } from '@angular/common/http';
import {
  ErrorHandler,
  Inject,
  InjectionToken,
  ModuleWithProviders,
  NgModule,
  Optional,
  SkipSelf,
} from '@angular/core';
import * as Sentry from '@sentry/browser';
import { SentryErrorHandler } from './sentry-error.handler';
import { SentryHttpInterceptor } from './sentry-http.interceptor';

export interface SentryConfig {
  environment: string;
  dsn: string;
}

export const SENTRY_CONFIG = new InjectionToken<SentryConfig>('SENTRY_CONFIG');

@NgModule()
export class SentryModule {
  constructor(
    @Optional() @SkipSelf() parentModule: SentryModule,
    @Inject(SENTRY_CONFIG) config: SentryConfig
  ) {
    // Check if NgModule was previously loaded
    // https://angular.io/guide/ngmodule-faq#how-can-i-tell-if-an-ngmodule-or-service-was-previously-loaded
    if (parentModule) {
      throw new Error(
        'SentryModule is already loaded. Import it in the AppModule only'
      );
    }

    if (!config) {
      throw new Error(
        'Provide config for SentryModule using the forRoot method'
      );
    }

    Sentry.init({
      dsn: config.dsn || '',
      environment: config.environment || 'unknown',
      allowUrls: [/^https:/i],
      ignoreErrors: [
        /^0 .*/,
        /400 GET.*\/property_owners\/contractors/,
        /400 GET.*\/property_owners\/validate_email/,
        /400 POST.*\/api\/property_searches\/contractor_landing/,
        /400 POST.*\/api\/property_searches\/public/,
        /401 GET.*\/api\/authentications\/sign/,
        /401 GET.*\/compliance-workflow\/timestamp\/.*/,
        /401 POST.*\/api\/authentications/,
        /401 POST.*\/api\/property_searches\/public/,
        /422 GET.*\/application_process_v3/,
        /422 POST.*\/api\/property_searches/,
        /422 POST.*\/application_process_v3\/.*\/address_validation/,
        /422 POST.*\/property_owners\/associations/,
        /422 PUT.*\/application_process_v3/,
        /422 GET.*\/application_process_v4/,
        /422 PUT.*\/application_process_v4/,
        /422 POST.*\/application_process_v4/,
        /422 GET.*\/portal_api\/.*\/contractor_improvements\.json/,
      ],
    });
  }

  static forRoot(config: SentryConfig): ModuleWithProviders<SentryModule> {
    return {
      ngModule: SentryModule,
      providers: [
        {
          provide: SENTRY_CONFIG,
          useValue: config,
        },
        { provide: ErrorHandler, useClass: SentryErrorHandler },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: SentryHttpInterceptor,
          multi: true,
        },
      ],
    };
  }
}
